import { formatCurrency } from "@tellescope/utils";
import { formatIntervalSuffix } from "./formatShopPrice";
import type { ShopPriceVariant } from "./types";

export interface SubscriptionCadenceTotal {
  /** Stable identity for a distinct billing cadence — same (interval, intervalCount) pair as its member rows. */
  key: string;
  amount: number;
  currency: string;
  /** e.g. "/mo", "/3mo" — matches the suffix a single variant of this same cadence would get from formatShopPrice. */
  suffix: string;
}

/**
 * Sums subscription row amounts per distinct billing cadence (interval + intervalCount) rather
 * than into one undifferentiated "monthly" bucket — a monthly and a quarterly subscription in the
 * same cart are different units and can't be added together into a single "/mo" total. Any variant
 * missing an interval (isSubscription true with no interval set, which the schema technically
 * permits) is grouped under its own "" suffix bucket rather than dropped, so its amount still
 * counts toward the cart/order total even though it renders with no cadence label.
 */
export const groupSubscriptionTotals = (
  t: (key: string, options: { ns: "shop" }) => string,
  rows: { variant: ShopPriceVariant; quantity: number }[]
): SubscriptionCadenceTotal[] => {
  const totalsByCadenceKey = new Map<string, SubscriptionCadenceTotal>();

  for (const { variant, quantity } of rows) {
    if (!variant.isSubscription) continue;
    const intervalCount = variant.intervalCount ?? 1;
    const key = variant.interval ? `${variant.interval}:${intervalCount}` : "";
    const existing = totalsByCadenceKey.get(key);
    const amount = variant.amount * quantity;

    if (existing) {
      existing.amount += amount;
    } else {
      totalsByCadenceKey.set(key, {
        key,
        amount,
        currency: variant.currency,
        suffix: variant.interval ? formatIntervalSuffix(t, variant.interval, intervalCount) : "",
      });
    }
  }

  return [...totalsByCadenceKey.values()];
};

/** Formats one cadence bucket's summed amount with its suffix, e.g. "$480.00/3mo". */
export const formatCadenceTotal = (total: SubscriptionCadenceTotal): string =>
  `${formatCurrency(total.amount, total.currency, { fromSmallestUnit: true })}${total.suffix}`;
